// @ts-check
import {  lightColorPalette } from '@linearb/linui-common';
import { Stack } from '@mui/material';
import { Body1 } from '@linearb/linui-common/typography';
import { useWizard } from 'react-use-wizard';
import {
  USER_DEFAULT_INTRESTS_BY_ROLE,
  USER_INTERESTS,
  USER_ROLES
} from '@linearb/linjs-common';
import { Checkbox } from '@linearb/linui-common/base';
import { useState } from 'react';
import { EditYourUserProfile } from './EditYourUserProfile';
import useUserProfile from 'views/root/views/home/hooks/useUserProfile';
import { STEPS } from './steps.consts';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { LBButton } from '@linearb/buzz';

/**
 * AddInterestsStep component renders a step where users can select their interests.
 * @returns {JSX.Element} The rendered component for the step that allows selecting interests.
 * @example
 * <AddInterestsStep />
 */
function AddInterestsStep() {
  const { nextStep, previousStep } = useWizard();
  const { onSave, userProfileDBData, accountId, orgId } = useUserProfile();
  const interestsInitialState =
    userProfileDBData?.interests ||
    USER_DEFAULT_INTRESTS_BY_ROLE[userProfileDBData?.role] ||
    USER_DEFAULT_INTRESTS_BY_ROLE[USER_ROLES.DEVELOPER];
  const [interests, setInterests] = useState(interestsInitialState);

  const handleOnNext = () => {
    onSave({ interests, currentStep: STEPS.ADD_WORK_HOURS });
    TrackingService.logEvent(TRACKING.EVENTS.USER_PROFILE.EVENT, {
      event_type: TRACKING.EVENTS.USER_PROFILE.EVENT_TYPE.ADD_INTERESTS,
      user_id: accountId,
      org_id: orgId,
      interests
    });
    nextStep();
  };

  const handleOnBack = () => {
    TrackingService.logEvent(TRACKING.EVENTS.USER_PROFILE.EVENT, {
      event_type: TRACKING.EVENTS.USER_PROFILE.EVENT_TYPE.BACK,
      user_id: accountId,
      org_id: orgId,
      back_from: STEPS.ADD_INTERESTS,
      back_to: STEPS.ADD_SENIORITY
    });
    previousStep();
  };

  const handleCheckboxChange = interest => {
    setInterests(prevSelected => {
      if (prevSelected.includes(interest)) {
        return prevSelected.filter(item => item !== interest);
      } else {
        return [...prevSelected, interest];
      }
    });
  };

  return (
    <>
      <Stack direction='column'>
        <Body1 color={lightColorPalette.lbTextSubdued}>
          Based on your inputs, we've highlighted some areas where we think
          LinearB can help you focus.
        </Body1>
        <EditYourUserProfile />
        <Body1
          color={lightColorPalette.lbTextSubdued}
          paddingTop={2}
          paddingBottom={1}
        >
          Feel free to edit the <strong>interest areas</strong> you'd like
          better insights on:
        </Body1>

        <Stack direction={'row'} flexWrap={'wrap'}>
          {USER_INTERESTS.map((interest) => {
            return (
              <Stack
                direction={'row'}
                marginRight={0.5}
                marginBottom={1}
                key={interest}
                data-cy={`user-profile-interest-${(interest || '')
                  .split(' ')
                  .join('-')}`}
              >
                <Checkbox
                  labelstyle={{
                    border: interests.includes(interest)
                      ? `1px solid ${lightColorPalette.lbBrightBlue_100}`
                      : `1px solid ${lightColorPalette.lbBlue_20}`,
                    padding: `5px`,
                    paddingRight: '1.2rem',
                    borderRadius: `5px`,
                    width: `fit-content`
                  }}
                  checkedIconColor={lightColorPalette.lbBrightBlue_100}
                  value={interests.includes(interest)}
                  onChange={() => handleCheckboxChange(interest)}
                >
                  <Body1 color={lightColorPalette.lbGreyDark}>{interest}</Body1>
                </Checkbox>
              </Stack>
            );
          })}
        </Stack>
      </Stack>

      <Stack direction='row' justifyContent='space-between' paddingTop={3}>
        <LBButton
          variant='ghost'
          onClick={handleOnBack}
          data-cy='user-profile-add-interest-back-button'
        >
          Back
        </LBButton>
        <LBButton
          disabled={!interests?.length}
          onClick={handleOnNext}
          data-cy='user-profile-configure-work-hours-button'
        >
          Next: Configure Work Hours
        </LBButton>
      </Stack>
    </>
  );
}

export default AddInterestsStep;
